import { A } from '@ember/array';
import Button from 'tio-ui/components/button';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import Form from 'tio-ui/components/form';
import { contains, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { HStack, VStack } from 'tio-ui/components/layout';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import * as v from 'valibot';
import Component from '@glimmer/component';
import ConfirmNameModal from './confirm-name-modal';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import formatPhone from 'tio-common/helpers/format-phone';
import PhoneInput, { parsePhoneNumber } from 'tio-ui/components/phone-input';
import type { PhoneCountryCode } from 'tio-ui/components/phone-input';
import PhoneVerificationFlow from 'tio-employee/components/app/phone-verification/flow';
import TioDialogDocumentAsset from 'tio-common/components/tio/dialog/document-asset';
import type { FormResult } from 'tio-ui/components/form';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { RegisterUser } from '../../../routes/enroll.ts';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type Owner from '@ember/owner';
import type PreRegisterService from 'tio-employee/services/pre-register.ts';
import type FeaturesService from 'tio-common/services/features';
import type RouterService from 'tio-employee/services/router.ts';
import type SessionService from 'tio-employee/services/session.ts';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type ZendeskService from 'tio-employee/services/zendesk.ts';
import { enrollUserRequest } from 'tio-data/requests/enrollment';
import { enrollFamilyMemberRequest } from 'tio-data/requests/enrollment';

export interface AuthRegisterEnrollFormSignature {
  Args: {
    registerUser: RegisterUser | null;
    email?: string;
    passwordMinLength: number;
  };
}

type EnrollFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  password: string;
  passwordConfirmation: string;
  familyInvite: boolean;
};

export default class AuthRegisterEnrollFormComponent extends Component<AuthRegisterEnrollFormSignature> {
  @service declare store: StoreService;
  @service declare session: SessionService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare zendesk: ZendeskService;
  @service declare notifications: NotificationsService;
  @service declare features: FeaturesService;

  @tracked formData: EnrollFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    passwordConfirmation: '',
    familyInvite: false,
  };

  @tracked isAgreedTioTerms = false;
  @tracked acceptedCustomTerms: number[] = A([]);
  @tracked persistName = false;
  @tracked formValidationErrors: Record<string, string[]> = {};
  @tracked hasSubmitted = false;
  @tracked showingTermsOfUse = false;
  @tracked showingPrivacyPolicy = false;
  @tracked showingConfirmNameModal = false;
  @tracked showConditionsError = false;
  /** E.164 as PhoneInput resolved it, or null when the number cannot be dialled. */
  @tracked capturePhoneE164: string | null = null;
  @tracked captureCountry: PhoneCountryCode = 'US';
  @tracked isVerifyingPhone = false;
  @tracked hasDeferredPhone = false;

  constructor(owner: Owner, args: AuthRegisterEnrollFormSignature['Args']) {
    super(owner, args);
    this.formData.email = args.email || '';
    this.formData.firstName = args.registerUser?.firstName || '';
    this.formData.lastName = args.registerUser?.lastName || '';
    this.formData.email = args.registerUser?.email || '';
    this.formData.phoneNumber = args.registerUser?.phoneNumber || '';
    this.formData.familyInvite = args.registerUser?.familyInvite || false;
  }

  get formSchema() {
    const passwordRegExp =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,64}$/;
    return v.pipe(
      v.object({
        firstName: v.pipe(v.string(), v.nonEmpty('First Name is Required')),
        lastName: v.pipe(v.string(), v.nonEmpty('Last Name is Required')),
        email: v.pipe(v.string(), v.email('Invalid Email Address')),
        phoneNumber: this.showPhoneCapture
          ? v.optional(v.string())
          : v.optional(
              v.union([
                v.literal(''), // allow empty string
                v.pipe(v.string(), v.regex(/^\+?[1-9]\d{9,14}$/, 'Invalid phone number format')),
              ])
            ),
        password: v.pipe(
          v.string(),
          v.regex(
            passwordRegExp,
            'Password must contain 1 upper case letter, 1 lower case letter, 1 number, and 1 special character'
          ),
          v.minLength(
            this.args.passwordMinLength,
            `Password must be at least ${this.args.passwordMinLength} characters long`
          ),
          v.maxLength(64, 'Password must not exceed 64 characters'),
          v.nonEmpty('Password is Required')
        ),
        passwordConfirmation: v.pipe(v.string(), v.nonEmpty('Password Confirmation is Required')),
      }),
      v.forward(
        v.partialCheck(
          [['password'], ['passwordConfirmation']],
          (input) => input.password === input.passwordConfirmation,
          'Passwords must match'
        ),
        ['passwordConfirmation']
      )
    );
  }

  validateField = (data: EnrollFormData) => {
    const result = v.safeParse(this.formSchema, data);

    if (result.success) {
      this.formValidationErrors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
    }
  };

  onFormChange = (result: FormResult<EnrollFormData>) => {
    this.formData = result.data;
    this.validateField(this.formData);
  };

  onPhoneFieldChange = (value: string) => {
    let cleaned = value.replace(/[^0-9]/g, '');

    // Limit the input to 10 digits (or 11 if it starts with "1")
    if ((cleaned.length > 10 && !cleaned.startsWith('1')) || cleaned.length > 11) {
      const length = cleaned.startsWith('1') ? 11 : 10;
      cleaned = cleaned.slice(0, length);
    }

    this.formData = { ...this.formData, phoneNumber: cleaned };
    this.validateField(this.formData);
  };

  onFormSubmit = (_result: FormResult<EnrollFormData>) => {
    this.hasSubmitted = true;
    this.validateField(this.formData);

    if (!this.isAgreed) {
      this.showConditionsError = true;
    }

    if (!this.canSubmit) {
      return;
    }

    const hasNameChanged = this.registerUserFullName && this.fullName !== this.registerUserFullName;
    if (hasNameChanged) {
      this.showingConfirmNameModal = true;
      return;
    }

    if (this.isFamilyInvite) {
      this.registerFamilyTask.perform();
    } else {
      this.registerTask.perform();
    }
  };

  get canSubmit() {
    return !Object.entries(this.formValidationErrors).length && this.isAgreed;
  }

  get showPhoneCapture() {
    return this.features.can('phoneCapture.surfaces.enrollment');
  }

  /**
   * What the enrollment payload carries. Falls back to the raw entry so an unparseable
   * number still reaches the server, which normalises it and answers with a warning
   * rather than refusing the account. Deliberately NOT the test for whether to verify:
   * only a dialable number can be sent a code.
   */
  get capturePhoneForPayload() {
    return this.capturePhoneE164 ?? this.resolvedCapturePhone ?? this.rawCapturePhone;
  }

  /**
   * PhoneInput reports E.164 on blur, and submitting with Enter from inside the field
   * never blurs it, so resolve the typed value here rather than depend on that timing.
   */
  get resolvedCapturePhone() {
    const typed = this.formData.phoneNumber;

    return typed ? (parsePhoneNumber(typed, this.captureCountry)?.e164 ?? null) : null;
  }

  /** What can actually be sent a code, however it was resolved. */
  get dialableCapturePhone() {
    return this.capturePhoneE164 ?? this.resolvedCapturePhone;
  }

  get rawCapturePhone() {
    return this.formData.phoneNumber?.length ? this.formData.phoneNumber : null;
  }

  /** The flow prefills from a string or collects one; it has no use for null. */
  get capturePhoneForFlow() {
    return this.capturePhoneForPayload ?? undefined;
  }

  setCapturePhone = (national: string) => {
    this.formData = { ...this.formData, phoneNumber: national };
    this.capturePhoneE164 = null;
    this.validateField(this.formData);
  };

  setValidCapturePhone = (e164: string | null) => {
    this.capturePhoneE164 = e164;
  };

  setCaptureCountry = (country: PhoneCountryCode) => {
    this.captureCountry = country;
  };

  onPhoneVerified = () => {
    this.router.transitionTo('/dashboard');
  };

  /** The flow renders its own confirmation; this only reveals the way out of it. */
  onPhoneDeferred = () => {
    this.hasDeferredPhone = true;
  };

  goToDashboard = () => {
    this.router.transitionTo('/dashboard');
  };

  get isFamilyInvite() {
    return this.formData.familyInvite;
  }

  get termsOfUse() {
    return this.preRegister.termsOfUse;
  }

  get privacyPolicy() {
    return this.preRegister.privacyPolicy;
  }

  get termsOfUseContent() {
    return this.termsOfUse?.fullText;
  }

  get privacyPolicyContent() {
    return this.privacyPolicy?.fullText;
  }

  get customTerms() {
    return this.args.registerUser?.settings?.customTerms || this.preRegister.customTerms || [];
  }

  get didAcceptAllCustomTerms() {
    return this.acceptedCustomTerms.length === this.customTerms.length;
  }

  get isAgreed() {
    return this.isAgreedTioTerms && this.didAcceptAllCustomTerms;
  }

  get fullName() {
    return `${this.formData.firstName} ${this.formData.lastName}`;
  }

  get registerUserFullName() {
    return this.args.registerUser
      ? `${this.args.registerUser.firstName} ${this.args.registerUser.lastName}`
      : '';
  }

  enforceUSPhonePrefix(input: string): string {
    const digits = input.replace(/\D/g, '');

    // Check if the cleaned number has 10 digits (add "1") or is an 11-digit starting with "1"
    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // If the input is not 10 digits (after cleaning), or 11 digits starting with "1", it's invalid
    throw new Error('Invalid phone number. Expected 10 digits or 11 digits starting with "1".');
  }

  registerTask = dropTask(async () => {
    if (!this.canSubmit) {
      return;
    }

    const query = location.search;
    const params = new URLSearchParams(query);
    const inviteCode = params.get('inviteCode');

    try {
      const enrollmentData = {
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        registrationEmail: this.formData.email,
        employeeInviteCode: inviteCode,
        password: this.formData.password,
        phoneNumber: this.showPhoneCapture
          ? this.capturePhoneForPayload
          : this.formData.phoneNumber?.length
            ? this.enforceUSPhonePrefix(this.formData.phoneNumber)
            : null,
        conditionsAccepted: [
          this.termsOfUse?.id,
          this.privacyPolicy?.id,
          ...this.acceptedCustomTerms,
        ].filter((id): id is string | number => id !== undefined && id !== null),
        persistName: this.persistName,
      };

      await this.store.request(enrollUserRequest(enrollmentData));

      await this.session.authenticate(
        'authenticator:database',
        this.formData.email,
        this.formData.password,
        {
          noTransitionAfterAuth: true,
        }
      );
      if (this.session.isAuthenticated) {
        // The account exists either way; only the number's status is outstanding.
        if (this.showPhoneCapture && this.dialableCapturePhone) {
          this.isVerifyingPhone = true;
          return;
        }

        this.router.transitionTo('/dashboard');
      }
    } catch (error) {
      this.notifications.add(error?.content?.error || 'An error occurred during enrollment', {
        appearance: 'error',
      });
    }
  });

  registerFamilyTask = dropTask(async () => {
    if (!this.canSubmit) {
      return;
    }

    const query = location.search;
    const params = new URLSearchParams(query);
    const inviteCode = params.get('inviteCode');

    try {
      const enrollmentData = {
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        registrationEmail: this.formData.email,
        employeeInviteCode: inviteCode,
        password: this.formData.password,
        phoneNumber: this.showPhoneCapture
          ? this.capturePhoneForPayload
          : this.formData.phoneNumber?.length
            ? this.enforceUSPhonePrefix(this.formData.phoneNumber)
            : null,
        conditionsAccepted: [
          this.termsOfUse?.id,
          this.privacyPolicy?.id,
          ...this.acceptedCustomTerms,
        ].filter((id): id is string | number => id !== undefined && id !== null),
        persistName: this.persistName,
      };

      await this.store.request(enrollFamilyMemberRequest(enrollmentData));

      await this.session.authenticate(
        'authenticator:database',
        this.formData.email,
        this.formData.password,
        {
          noTransitionAfterAuth: true,
        }
      );
      if (this.session.isAuthenticated) {
        // The account exists either way; only the number's status is outstanding.
        if (this.showPhoneCapture && this.dialableCapturePhone) {
          this.isVerifyingPhone = true;
          return;
        }

        this.router.transitionTo('/dashboard');
      }
    } catch (error) {
      this.notifications.add(error?.content?.error || 'An error occurred during enrollment', {
        appearance: 'error',
      });
    }
  });

  didChangeAcceptanceOfCustomTerm = (condition: ConditionModel) => {
    // @ts-expect-error who knows
    const index = this.acceptedCustomTerms.indexOf(condition.id);
    // Add or remove the condition ID from the list of accepted custom terms
    index === -1
      ? // @ts-expect-error who knows
        (this.acceptedCustomTerms = [...this.acceptedCustomTerms, condition.id])
      : // @ts-expect-error who knows
        (this.acceptedCustomTerms = this.acceptedCustomTerms.filter((id) => id !== condition.id));

    if (this.isAgreed) {
      this.showConditionsError = false;
    } else if (this.hasSubmitted) {
      this.showConditionsError = true;
    }
  };

  onChangeTioTerms = (isChecked: boolean) => {
    this.isAgreedTioTerms = isChecked;
    if (this.isAgreed) {
      this.showConditionsError = false;
    } else if (this.hasSubmitted) {
      this.showConditionsError = true;
    }
  };

  showConditionsDialog = (type: string, event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    switch (type) {
      case 'terms':
        this.showingTermsOfUse = true;
        break;
      case 'privacy':
        this.showingPrivacyPolicy = true;
        break;
    }
  };

  onCloseConfirmNameModal = () => {
    this.showingConfirmNameModal = false;
    this.zendesk.openWebChatWindow();
  };

  submitWithPersistedName = () => {
    this.showingConfirmNameModal = false;
    this.persistName = true;

    if (this.isFamilyInvite) {
      this.registerFamilyTask.perform();
    } else {
      this.registerTask.perform();
    }
  };

  <template>
    {{#if this.isVerifyingPhone}}
      <div class="tio-col-loose" data-test-enroll-verify>
        {{! Opens at the number step with the entry prefilled rather than at the code
            step: only an explicit send starts a challenge, so entering at `code` would
            show boxes for a code nothing had sent. }}
        <PhoneVerificationFlow
          @surface="enrollment"
          @phoneNumber={{this.capturePhoneForFlow}}
          @confirmationTitle={{t "login.phone_capture.all_set_title"}}
          @confirmationBody={{t
            "login.phone_capture.all_set_body"
            number=this.formData.phoneNumber
          }}
          @onVerified={{this.onPhoneVerified}}
          @onDeferred={{this.onPhoneDeferred}}
        />

        {{#if this.hasDeferredPhone}}
          <div class="flex">
            <Button
              @intent="primary"
              class="my-4 mx-auto"
              {{on "click" this.goToDashboard}}
              data-test-enroll-go-to-dashboard
            >
              {{t "login.phone_capture.go_to_dashboard"}}
            </Button>
          </div>
        {{/if}}
      </div>
    {{else}}
      <Form
        @data={{this.formData}}
        @onChange={{this.onFormChange}}
        @onSubmit={{this.onFormSubmit}}
        as |form|
      >
        <VStack>
          <HStack>
            {{#let (form.field "firstName") as |f|}}
              <Input
                @label={{t "login.first_name"}}
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @hint={{t "login.required"}}
                @errors={{errorsForValibotField "firstName" schemaErrors=this.formValidationErrors}}
                @classes={{hash base="w-full"}}
                name="firstName"
                @isRequired={{true}}
                data-test-first-name
              />
            {{/let}}
            {{#let (form.field "lastName") as |f|}}
              <Input
                @label={{t "login.last_name"}}
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @hint={{t "login.required"}}
                @errors={{errorsForValibotField "lastName" schemaErrors=this.formValidationErrors}}
                @classes={{hash base="w-full"}}
                name="lastName"
                @isRequired={{true}}
                data-test-last-name
              />
            {{/let}}
          </HStack>
          <HStack>
            {{#let (form.field "email") as |f|}}
              <Input
                @label={{t "login.email"}}
                @value={{f.value}}
                @onChange={{f.onChange}}
                @onBlur={{f.onBlur}}
                @hint={{t "login.email_hint"}}
                @errors={{errorsForValibotField "email" schemaErrors=this.formValidationErrors}}
                @classes={{hash base="w-full"}}
                @type="email"
                name="email"
                @isRequired={{true}}
                data-test-email
              />
            {{/let}}
            {{#if this.showPhoneCapture}}
              <PhoneInput
                @testHook="enroll-phone"
                @label={{t "login.mobile"}}
                @value={{or this.formData.phoneNumber ""}}
                @onChange={{this.setCapturePhone}}
                @onValidChange={{this.setValidCapturePhone}}
                @onCountryChange={{this.setCaptureCountry}}
                @hint={{t "login.phone_capture.mobile_hint"}}
                @isOptional={{true}}
              />
            {{else}}
              <Input
                @label={{t "login.mobile"}}
                @value={{formatPhone (or this.formData.phoneNumber "")}}
                @onChange={{this.onPhoneFieldChange}}
                @hint={{t "login.mobile_hint"}}
                @errors={{errorsForValibotField
                  "phoneNumber"
                  schemaErrors=this.formValidationErrors
                }}
                @classes={{hash base="w-full"}}
                name="phoneNumber"
                data-test-phone
              />
            {{/if}}
          </HStack>
          {{#let (form.field "password") as |f|}}
            <Input
              @label={{t "login.create_password"}}
              @value={{f.value}}
              @onChange={{f.onChange}}
              @onBlur={{f.onBlur}}
              @hint={{t "login.minimum_characters" min=@passwordMinLength}}
              @errors={{errorsForValibotField
                "password"
                schemaErrors=this.formValidationErrors
                hasSubmitted=this.hasSubmitted
              }}
              @type="password"
              name="password"
              @isRequired={{true}}
              data-test-password
            />
          {{/let}}
          {{#let (form.field "passwordConfirmation") as |f|}}
            <Input
              @label={{t "login.reenter_password"}}
              @value={{f.value}}
              @onChange={{f.onChange}}
              @onBlur={{f.onBlur}}
              @hint={{t "login.minimum_characters" min=@passwordMinLength}}
              @errors={{errorsForValibotField
                "passwordConfirmation"
                schemaErrors=this.formValidationErrors
                hasSubmitted=this.hasSubmitted
              }}
              @type="password"
              name="passwordConfirmation"
              @isRequired={{true}}
              data-test-password-confirmation
            />
          {{/let}}
          <div class="flex mb-4">
            <Checkbox
              data-test-enroll-tio-conditions
              @checked={{this.isAgreedTioTerms}}
              @onChange={{this.onChangeTioTerms}}
              aria-labelledby="appTermsAndPrivacy"
            />
            <div id="appTermsAndPrivacy" class="p-0">
              {{t "login.i_have_read_and_agree_to_tuitions"}}
              <button
                class="app-text-link"
                type="button"
                data-test-enroll-form-terms-link
                {{on "click" (fn this.showConditionsDialog "terms")}}
              >
                {{t "login.terms_of_service"}}
              </button>
              {{t "and"}}
              <button
                class="app-text-link"
                type="button"
                data-test-enroll-form-privacy-link
                {{on "click" (fn this.showConditionsDialog "privacy")}}
              >
                {{t "login.privacy_policy"}}.
              </button>
            </div>
          </div>
          {{#each this.customTerms as |condition|}}
            <div class="flex mb-4">
              <Checkbox
                data-test-enroll-condition
                @size="lg"
                @checked={{contains this.acceptedCustomTerms condition.id}}
                @onChange={{fn this.didChangeAcceptanceOfCustomTerm condition}}
                aria-labelledby={{condition.id}}
              />
              <span id={{condition.id}}>
                {{htmlSafe condition.fullText}}
              </span>
            </div>
          {{/each}}
          {{#if this.showConditionsError}}
            <div class="tio-fg-danger text-sm mt-2" data-test-conditions-error>
              {{t "login.please_agree_to_all_terms"}}
            </div>
          {{/if}}
          {{#if this.showPhoneCapture}}
            <p
              class="tio-text-xs-normal tio-fg-muted border-t tio-border-subtle pt-3"
              data-test-enroll-sms-consent
            >
              {{t "login.phone_capture.sms_consent"}}
            </p>
          {{/if}}
          <div class="flex">
            <Button
              @intent="primary"
              type="submit"
              @isRunning={{or this.registerTask.isRunning this.registerFamilyTask.isRunning}}
              class="my-4 mx-auto"
            >
              {{t "login.get_started"}}
            </Button>
          </div>
        </VStack>
      </Form>

      <ConfirmNameModal
        @show={{this.showingConfirmNameModal}}
        @onClose={{this.onCloseConfirmNameModal}}
        @employeeFullName={{this.registerUserFullName}}
        @inputName={{this.fullName}}
        @onSubmit={{this.submitWithPersistedName}}
      />

      <TioDialogDocumentAsset
        @title="Privacy Policy"
        {{! @glint-expect-error: should privacy be optional? }}
        @assetName={{this.privacyPolicy.staticJsonAssetName}}
        @content={{this.privacyPolicyContent}}
        @show={{this.showingPrivacyPolicy}}
        @onCancel={{fn (mut this.showingPrivacyPolicy) false}}
      />

      <TioDialogDocumentAsset
        @title="Terms of Service"
        {{! @glint-expect-error: should privacy be optional? }}
        @assetName={{this.termsOfUse.staticJsonAssetName}}
        @content={{this.termsOfUseContent}}
        @show={{this.showingTermsOfUse}}
        @onCancel={{fn (mut this.showingTermsOfUse) false}}
      />
    {{/if}}
  </template>
}
